import { Link } from "react-router-dom";
import { Check, LogOut, Monitor, Moon, Palette, Settings, Sun } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useTheme, type ThemeMode } from "@/lib/theme";
import { cn } from "@/lib/utils";

type Props = {
  /** Display name — falls back to email if no name is set. */
  name: string;
  email?: string;
  roleLabel?: string;
  workspaceName?: string;
  settingsTo?: string;
  onLogout: () => void;
  isLoggingOut?: boolean;
  className?: string;
};

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function UserMenu({
  name,
  email,
  roleLabel,
  workspaceName,
  settingsTo,
  onLogout,
  isLoggingOut,
  className,
}: Props) {
  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className={cn(
                "grid h-11 w-11 place-items-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                className
              )}
            >
              <Avatar className="h-9 w-9 border border-border/60">
                <AvatarFallback>{initialsFrom(name)}</AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Account</TooltipContent>
      </Tooltip>

      <DropdownMenuContent side="right" align="end" alignOffset={-4}>
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-foreground">{name}</span>
          {email && <span className="truncate text-xs font-normal text-muted-foreground">{email}</span>}
        </DropdownMenuLabel>
        {(roleLabel || workspaceName) && (
          <div className="px-2.5 pb-1.5 text-xs text-muted-foreground">
            {roleLabel}
            {roleLabel && workspaceName && " · "}
            {workspaceName}
          </div>
        )}
        <DropdownMenuSeparator />
        {settingsTo && (
          <DropdownMenuItem asChild>
            <Link to={settingsTo}>
              <Settings />
              Settings
            </Link>
          </DropdownMenuItem>
        )}
        {/* Theme submenu — mirrors the desktop TopBar toggle. On mobile the
            TopBar copy is hidden, so this is the only place to switch themes
            on phone. Kept on desktop too (per user request — two locations). */}
        <ThemeMenuItem />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onLogout();
          }}
          disabled={isLoggingOut}
        >
          <LogOut />
          {isLoggingOut ? "Signing out…" : "Log out"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Theme submenu ────────────────────────────────────────────────────────────

const THEME_OPTIONS: { value: ThemeMode; icon: typeof Sun; label: string }[] = [
  { value: "dark", icon: Moon, label: "Dark" },
  { value: "light", icon: Sun, label: "Light" },
  { value: "system", icon: Monitor, label: "System" },
];

function ThemeMenuItem() {
  const { mode, setMode } = useTheme();
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Palette />
        Theme
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {THEME_OPTIONS.map(({ value, icon: Icon, label }) => {
          const active = mode === value;
          return (
            <DropdownMenuItem
              key={value}
              onSelect={(e) => {
                e.preventDefault();
                setMode(value);
              }}
            >
              <Icon />
              <span className="flex-1">{label}</span>
              {active && <Check className="h-4 w-4 text-muted-foreground" strokeWidth={2} />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
